import React, { useState, useEffect } from 'react'
import { promptApi } from '../services/api'

interface PromptTemplate {
  id: number
  name: string
  prompt_text: string
  function_schema?: any
  is_active: boolean
  description?: string
  created_at: string
  updated_at: string
}

interface SchemaProperty {
  type: string
  description: string
  enum?: string[]
  items?: any
  properties?: { [key: string]: SchemaProperty }
  required?: string[]
  format?: string
}

interface FunctionSchema {
  name: string
  description: string
  parameters: {
    type: string
    properties: { [key: string]: SchemaProperty }
    required: string[]
  }
}

const PromptsPage: React.FC = () => {
  const [activePrompt, setActivePrompt] = useState<PromptTemplate | null>(null)
  const [allPrompts, setAllPrompts] = useState<PromptTemplate[]>([])
  const [editingPrompt, setEditingPrompt] = useState<string>('')
  const [editingName, setEditingName] = useState<string>('')
  const [editingDescription, setEditingDescription] = useState<string>('')
  const [editingSchema, setEditingSchema] = useState<FunctionSchema | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showNewPrompt, setShowNewPrompt] = useState(false)
  const [activeTab, setActiveTab] = useState<'prompt' | 'schema'>('prompt')
  const [schemaValidation, setSchemaValidation] = useState<{ valid: boolean; errors: string[]; warnings: string[] } | null>(null)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const getDefaultSchema = (): FunctionSchema => ({
    name: "analyse_mining_news_signal",
    description: "Extract structured sales-signal insights from a mining news article",
    parameters: {
      type: "object",
      properties: {
        signal_summary: {
          type: "string",
          description: "Brief 1-2 sentence summary of the key signal or opportunity"
        },
        expanded_summary: {
          type: "string",
          description: "Detailed 3-4 sentence analysis of the mining opportunity and business implications"
        },
        drilling_planned: {
          type: "string",
          enum: ["Yes", "No"],
          description: "Whether drilling activities are planned or mentioned"
        },
        drilling_timeline: {
          type: "string",
          description: "Timeline for drilling activities if mentioned (e.g., 'Q2 2024', 'Next month')"
        },
        drilling_details: {
          type: "string",
          description: "Specific details about drilling activities, targets, or objectives"
        },
        drilling_length_meters: {
          type: "string",
          description: "Total drilling length in meters if specified"
        },
        additional_sales_signals: {
          type: "array",
          items: { type: "string" },
          description: "Other business opportunities or signals beyond drilling (e.g., feasibility studies, partnerships, funding)"
        },
        contact_details: {
          type: "array",
          items: {
            type: "object",
            properties: {
              first_name: { type: "string", description: "Contact's first name" },
              last_name: { type: "string", description: "Contact's last name" },
              email: { type: "string", format: "email", description: "Email address if mentioned" },
              job_title: { type: "string", description: "Job title or position" },
              phone_number: { type: "string", description: "Phone number if provided" }
            },
            required: ["first_name", "last_name"]
          },
          description: "Extract any contact details mentioned in the article"
        },
        buying_intent_score: {
          type: "string",
          enum: ["Low", "Medium", "High", "Very High"],
          description: "Assessment of potential buying intent based on drilling activity, timeline, and company stage"
        },
        talking_points: {
          type: "array",
          items: { type: "string" },
          description: "Key talking points for sales conversations based on the article content"
        },
        email_subject: {
          type: "string",
          description: "Compelling email subject line for outreach based on the signal"
        },
        email_body: {
          type: "string",
          description: "Professional email body for outreach that references the specific signal and offers relevant drilling services"
        }
      },
      required: [
        "signal_summary",
        "expanded_summary", 
        "drilling_planned",
        "additional_sales_signals",
        "buying_intent_score",
        "talking_points",
        "email_subject",
        "email_body"
      ]
    }
  })

  const validateSchema = async () => {
    if (!editingSchema) {
      setSchemaValidation({
        valid: false,
        errors: ['No schema to validate'],
        warnings: []
      })
      return
    }
    
    try {
      console.log('Validating schema:', editingSchema)
      const result = await promptApi.validateSchema(editingSchema)
      console.log('Validation result:', result)
      setSchemaValidation(result.data)
    } catch (error: any) {
      console.error('Schema validation failed:', error)
      
      // Better error handling
      let errorMessage = 'Failed to validate schema'
      if (error?.response) {
        // API returned an error response
        if (error.response.status === 401) {
          errorMessage = 'Authentication required - please login'
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid request - check schema format'
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail
        } else {
          errorMessage = `Server error: ${error.response.status}`
        }
      } else if (error?.request) {
        // Request was made but no response received
        errorMessage = 'Network error - cannot reach server'
      } else {
        // Something else happened
        errorMessage = error?.message || 'Unknown error occurred'
      }
      
      setSchemaValidation({
        valid: false,
        errors: [errorMessage],
        warnings: ['Check console for more details']
      })
    }
  }

  const loadPrompts = async () => {
    try {
      setIsLoading(true)
      console.log('Loading prompts...')
      const [active, all] = await Promise.all([
        promptApi.getActivePrompt().catch((error) => {
          console.error('Failed to load active prompt:', error)
          return null
        }),
        promptApi.getAllPrompts().catch((error) => {
          console.error('Failed to load all prompts:', error)
          return []
        })
      ])
      
      console.log('Loaded active prompt:', active)
      console.log('Loaded all prompts:', all)
      
      setActivePrompt(active)
      setAllPrompts(all || [])
      
      if (active) {
        setEditingPrompt(active.prompt_text)
        setEditingName(active.name)
        setEditingDescription(active.description || '')
        // Use the stored schema from database, fallback to default only if none exists
        if (active.function_schema) {
          setEditingSchema(active.function_schema)
        } else {
          // No schema in database, use the comprehensive default
          setEditingSchema(getDefaultSchema())
        }
      } else {
        // No active prompt, initialize with default
        setEditingSchema(getDefaultSchema())
      }
    } catch (error) {
      console.error('Failed to load prompts:', error)
      showMessage('error', 'Failed to load prompt templates')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPrompts()
  }, [])

  const handleSavePrompt = async () => {
    if (!editingPrompt.trim() || !editingName.trim()) {
      showMessage('error', 'Name and prompt text are required')
      return
    }

    try {
      setIsSaving(true)
      
      if (activePrompt) {
        // Update existing prompt
        await promptApi.updatePrompt(activePrompt.id, {
          name: editingName,
          prompt_text: editingPrompt,
          description: editingDescription,
          function_schema: editingSchema
        })
        showMessage('success', 'Prompt updated successfully')
      } else {
        // Create new prompt
        await promptApi.createPrompt({
          name: editingName,
          prompt_text: editingPrompt,
          description: editingDescription,
          function_schema: editingSchema,
          set_as_active: true
        })
        showMessage('success', 'Prompt created successfully')
      }
      
      loadPrompts()
    } catch (error) {
      console.error('Failed to save prompt:', error)
      showMessage('error', 'Failed to save prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleActivatePrompt = async (promptId: number) => {
    try {
      await promptApi.activatePrompt(promptId)
      showMessage('success', 'Prompt activated successfully')
      loadPrompts()
    } catch (error) {
      console.error('Failed to activate prompt:', error)
      showMessage('error', 'Failed to activate prompt')
    }
  }

  const handleCreateNew = () => {
    setShowNewPrompt(true)
    setEditingName('')
    setEditingPrompt('')
    setEditingDescription('')
    setEditingSchema(getDefaultSchema())
    setActiveTab('prompt')
  }

  const handleCancelNew = () => {
    setShowNewPrompt(false)
    if (activePrompt) {
      setEditingPrompt(activePrompt.prompt_text)
      setEditingName(activePrompt.name)
      setEditingDescription(activePrompt.description || '')
      // Restore the original schema from the active prompt
      if (activePrompt.function_schema) {
        setEditingSchema(activePrompt.function_schema)
      } else {
        setEditingSchema(getDefaultSchema())
      }
    }
  }

  const addSchemaProperty = () => {
    if (!editingSchema) return
    
    const newPropertyName = `new_field_${Object.keys(editingSchema.parameters.properties).length + 1}`
    const updatedSchema = {
      ...editingSchema,
      parameters: {
        ...editingSchema.parameters,
        properties: {
          ...editingSchema.parameters.properties,
          [newPropertyName]: {
            type: "string",
            description: "Description for the new field"
          }
        }
      }
    }
    setEditingSchema(updatedSchema)
  }

  const removeSchemaProperty = (propertyName: string) => {
    if (!editingSchema) return
    
    const { [propertyName]: removed, ...remainingProperties } = editingSchema.parameters.properties
    const updatedSchema = {
      ...editingSchema,
      parameters: {
        ...editingSchema.parameters,
        properties: remainingProperties,
        required: editingSchema.parameters.required.filter(req => req !== propertyName)
      }
    }
    setEditingSchema(updatedSchema)
  }

  const updateSchemaProperty = (propertyName: string, newName: string, property: SchemaProperty) => {
    if (!editingSchema) return
    
    const { [propertyName]: removed, ...otherProperties } = editingSchema.parameters.properties
    const updatedSchema = {
      ...editingSchema,
      parameters: {
        ...editingSchema.parameters,
        properties: {
          ...otherProperties,
          [newName]: property
        },
        required: editingSchema.parameters.required.map(req => req === propertyName ? newName : req)
      }
    }
    setEditingSchema(updatedSchema)
  }

  const toggleRequiredProperty = (propertyName: string) => {
    if (!editingSchema) return
    
    const isCurrentlyRequired = editingSchema.parameters.required.includes(propertyName)
    const updatedRequired = isCurrentlyRequired
      ? editingSchema.parameters.required.filter(req => req !== propertyName)
      : [...editingSchema.parameters.required, propertyName]
    
    const updatedSchema = {
      ...editingSchema,
      parameters: {
        ...editingSchema.parameters,
        required: updatedRequired
      }
    }
    setEditingSchema(updatedSchema)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Prompt Management</h1>
            <p className="text-gray-600 mt-1">
              Configure AI prompts used for article analysis and signal extraction.
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Create New Prompt
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {message.type === 'success' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                )}
              </svg>
              {message.text}
            </div>
          </div>
        )}

        {/* Current Active Prompt Info */}
        {activePrompt && !showNewPrompt && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <div>
                <p className="text-green-800 font-medium">Currently Active: {activePrompt.name}</p>
                <p className="text-green-600 text-sm">Last updated: {new Date(activePrompt.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Prompt & Schema Editor */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {showNewPrompt ? 'Create New Prompt' : 'Edit Active Prompt'}
          </h2>
          
          {/* Tab Navigation */}
          <div className="flex border border-gray-200 rounded-lg">
            <button
              onClick={() => setActiveTab('prompt')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                activeTab === 'prompt'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Prompt Text
            </button>
            <button
              onClick={() => setActiveTab('schema')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                activeTab === 'schema'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Output Schema
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Name Input - Always visible */}
          <div>
            <label htmlFor="prompt-name" className="block text-sm font-medium text-gray-700 mb-2">
              Prompt Name
            </label>
            <input
              id="prompt-name"
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter prompt name..."
            />
          </div>

          {/* Description Input - Always visible */}
          <div>
            <label htmlFor="prompt-description" className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <input
              id="prompt-description"
              type="text"
              value={editingDescription}
              onChange={(e) => setEditingDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter prompt description..."
            />
          </div>

          {/* Tab Content */}
          {activeTab === 'prompt' && (
            <>
              {/* Prompt Text Editor */}
              <div>
                <label htmlFor="prompt-text" className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt
                </label>
                <textarea
                  id="prompt-text"
                  value={editingPrompt}
                  onChange={(e) => setEditingPrompt(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Enter your system prompt here..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  This prompt will guide how the AI analyzes mining news articles and extracts structured signals.
                </p>
              </div>

              {/* Character Count */}
              <div className="text-right">
                <span className="text-sm text-gray-500">
                  {editingPrompt.length} characters
                </span>
              </div>
            </>
          )}

          {activeTab === 'schema' && editingSchema && (
            <>
              {/* Schema Editor */}
              <div className="space-y-6">
                {/* Schema Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Function Name
                    </label>
                    <input
                      type="text"
                      value={editingSchema.name}
                      onChange={(e) => setEditingSchema({
                        ...editingSchema,
                        name: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Function name..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Function Description
                    </label>
                    <input
                      type="text"
                      value={editingSchema.description}
                      onChange={(e) => setEditingSchema({
                        ...editingSchema,
                        description: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="What this function does..."
                    />
                  </div>
                </div>

                {/* Output Fields */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-md font-medium text-gray-900">Output Fields</h3>
                    <button
                      onClick={addSchemaProperty}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      + Add Field
                    </button>
                  </div>

                  <div className="space-y-4">
                    {editingSchema.parameters?.properties && Object.entries(editingSchema.parameters.properties).map(([propName, property]) => (
                      <div key={propName} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        {/* Top row: Field Name, Type, and Actions */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
                          {/* Field Name - Narrower */}
                          <div className="md:col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Field Name</label>
                            <input
                              type="text"
                              value={propName}
                              onChange={(e) => {
                                const newName = e.target.value
                                updateSchemaProperty(propName, newName, property)
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          {/* Field Type - Narrower */}
                          <div className="md:col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                            <select
                              value={property.type}
                              onChange={(e) => updateSchemaProperty(propName, propName, {
                                ...property,
                                type: e.target.value
                              })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="integer">Integer</option>
                              <option value="boolean">Boolean</option>
                              <option value="array">Array</option>
                              <option value="object">Object</option>
                            </select>
                          </div>

                          {/* Actions - Right side */}
                          <div className="md:col-span-5 flex items-end justify-end space-x-3">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={editingSchema.parameters.required.includes(propName)}
                                onChange={() => toggleRequiredProperty(propName)}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">Required</span>
                            </label>
                            <button
                              onClick={() => removeSchemaProperty(propName)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {/* Description on new line - Much bigger */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <textarea
                            value={property.description}
                            onChange={(e) => updateSchemaProperty(propName, propName, {
                              ...property,
                              description: e.target.value
                            })}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Detailed description of what this field should contain and how it will be used..."
                          />
                        </div>

                        {/* Array/Enum specific options */}
                        {property.type === 'array' && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Array Item Type</label>
                            <select
                              value={property.items?.type || 'string'}
                              onChange={(e) => updateSchemaProperty(propName, propName, {
                                ...property,
                                items: { type: e.target.value }
                              })}
                              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="object">Object</option>
                            </select>
                          </div>
                        )}

                        {property.enum && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Enum Values (comma-separated)</label>
                            <input
                              type="text"
                              value={property.enum.join(', ')}
                              onChange={(e) => updateSchemaProperty(propName, propName, {
                                ...property,
                                enum: e.target.value.split(',').map(v => v.trim())
                              })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Option1, Option2, Option3"
                            />
                          </div>
                        )}

                        {property.type === 'string' && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={!!property.enum}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updateSchemaProperty(propName, propName, {
                                      ...property,
                                      enum: ['Option 1', 'Option 2']
                                    })
                                  } else {
                                    const { enum: removed, ...propertyWithoutEnum } = property
                                    updateSchemaProperty(propName, propName, propertyWithoutEnum)
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className="text-xs text-gray-700">Use as dropdown (enum)</span>
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schema Validation */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-md font-medium text-gray-900">Schema Validation</h3>
                    <button
                      onClick={validateSchema}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      Validate Schema
                    </button>
                  </div>

                  {schemaValidation && (
                    <div className={`p-3 rounded-lg ${
                      schemaValidation.valid 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center mb-2">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          schemaValidation.valid ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className={`font-medium ${
                          schemaValidation.valid ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {schemaValidation.valid ? 'Schema is valid' : 'Schema has errors'}
                        </span>
                      </div>
                      
                      {schemaValidation.errors.length > 0 && (
                        <div className="text-red-700 text-sm">
                          <ul className="list-disc list-inside">
                            {schemaValidation.errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Schema Preview */}
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Schema Preview</h3>
                  <pre className="bg-gray-100 p-3 rounded-lg text-xs overflow-auto max-h-60">
                    {JSON.stringify(editingSchema, null, 2)}
                  </pre>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            {showNewPrompt && (
              <button
                onClick={handleCancelNew}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSavePrompt}
              disabled={isSaving || !editingPrompt.trim() || !editingName.trim()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
            >
              {isSaving ? 'Saving...' : (showNewPrompt ? 'Create Prompt' : 'Save Changes')}
            </button>
          </div>
        </div>
      </div>

      {/* All Prompts List */}
      {allPrompts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">All Prompt Templates</h2>
          <div className="space-y-3">
            {allPrompts.map((prompt) => (
              <div
                key={prompt.id}
                className={`border rounded-lg p-4 ${
                  prompt.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900">{prompt.name}</h3>
                      {prompt.is_active && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </div>
                    {prompt.description && (
                      <p className="text-sm text-gray-600 mt-1">{prompt.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Created: {new Date(prompt.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {!prompt.is_active && (
                    <button
                      onClick={() => handleActivatePrompt(prompt.id)}
                      className="ml-4 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                      Activate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PromptsPage
